/**
 * 待买清单页。
 *
 * 闭环的最后一环：生成菜谱时缺的材料自动落在这里，
 * 买回来勾一下 → 一键入库 → 变成冰箱里的食材。
 * 少了"一键入库"，用户买完还得手工再录一遍，这功能就是添乱。
 *
 * 整行点击 = 勾选（这是本页最高频动作），左滑 = 删除。
 */

import type { BuyItem } from '@fridge/shared';
import { useState } from 'react';

import {
	ApiError,
	addBuyItems,
	clearCheckedBuyItems,
	deleteBuyItem,
	moveBuyItemsToFridge,
	patchBuyItem,
} from '../api/client';
import { EmptyState, SkeletonRow } from '../components/Feedback';
import { GLYPHS, IconGlyph } from '../components/IconGlyph';
import { PageHeader } from '../components/PageHeader';
import { Pressable } from '../components/Pressable';
import { useToast } from '../components/Toast';
import { animateExit, useListFlip, useStaggerIn, useSwipeReveal } from '../motion';
import { useAppData } from '../state/app-context';

const REVEAL_WIDTH = 92;

export function BuyListPage(): JSX.Element {
	const { buyList, refreshBuyList, refreshFridge, loading } = useAppData();
	const toast = useToast();

	const [name, setName] = useState('');
	const [amount, setAmount] = useState('');
	const [importing, setImporting] = useState(false);

	const items = buyList?.items ?? [];
	const checked = items.filter((item) => item.checked);

	const staggerRef = useStaggerIn<HTMLDivElement>([items.length]);
	const flipRef = useListFlip<HTMLUListElement>([items.map((item) => item.id).join('|')]);

	async function handleAdd(): Promise<void> {
		const trimmed = name.trim();
		if (!trimmed) return;
		try {
			await addBuyItems([{ name: trimmed, amount: amount.trim() || null }]);
			setName('');
			setAmount('');
			await refreshBuyList();
		} catch (error) {
			toast.show(error instanceof ApiError ? error.message : '添加失败', 'error');
		}
	}

	async function handleToggle(item: BuyItem): Promise<void> {
		try {
			await patchBuyItem(item.id, { checked: !item.checked });
			await refreshBuyList();
		} catch (error) {
			toast.show(error instanceof ApiError ? error.message : '操作失败', 'error');
		}
	}

	function handleDelete(item: BuyItem): void {
		const row = flipRef.current?.querySelector<HTMLElement>(`[data-flip-id="${item.id}"]`);
		const commit = async (): Promise<void> => {
			try {
				await deleteBuyItem(item.id);
			} catch (error) {
				toast.show(error instanceof ApiError ? error.message : '删除失败', 'error');
			}
			await refreshBuyList();
		};
		if (row) animateExit(row, () => void commit(), 1);
		else void commit();
	}

	async function handleImport(): Promise<void> {
		if (checked.length === 0) {
			toast.show('先勾上已经买回来的那些。', 'warn');
			return;
		}
		setImporting(true);
		try {
			const result = await moveBuyItemsToFridge();
			toast.show(
				`入库 ${result.imported} 样${result.merged > 0 ? `，${result.merged} 样并入了已有条目` : ''}。`,
				'success',
			);
			await Promise.all([refreshBuyList(), refreshFridge()]);
		} catch (error) {
			toast.show(error instanceof ApiError ? error.message : '入库失败', 'error');
		} finally {
			setImporting(false);
		}
	}

	async function handleClearChecked(): Promise<void> {
		try {
			const { removed } = await clearCheckedBuyItems();
			toast.show(`清掉了 ${removed} 条。`, 'info');
			await refreshBuyList();
		} catch (error) {
			toast.show(error instanceof ApiError ? error.message : '清理失败', 'error');
		}
	}

	const remaining = items.length - checked.length;

	return (
		<div ref={staggerRef}>
			<PageHeader
				title="待买"
				subtitle={
					items.length === 0
						? '清单是空的'
						: `还差 ${remaining} 样${checked.length > 0 ? `，已买回 ${checked.length} 样` : ''}`
				}
			/>

			<div className="mb-4 flex gap-2 px-4">
				<input
					className="field flex-1"
					placeholder="要买什么"
					value={name}
					onChange={(event) => setName(event.target.value)}
					onKeyDown={(event) => {
						if (event.key === 'Enter') void handleAdd();
					}}
				/>
				<input
					className="field w-[5.5rem] text-center"
					placeholder="数量"
					value={amount}
					onChange={(event) => setAmount(event.target.value)}
				/>
				<Pressable
					aria-label="加入清单"
					onClick={() => void handleAdd()}
					disabled={!name.trim()}
					className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-input bg-cta text-on-cta disabled:opacity-40"
				>
					<IconGlyph glyph={GLYPHS.add} size={18} weight="bold" />
				</Pressable>
			</div>

			{loading && items.length === 0 ? (
				<div className="mx-4 overflow-hidden rounded-card shadow-e1">
					<SkeletonRow count={3} />
				</div>
			) : null}

			{!loading && items.length === 0 ? (
				<EmptyState
					glyph={GLYPHS.buy}
					title="清单是空的"
					hint="在上面加一条，或者去做菜页生成菜谱，缺的材料会自动出现在这里。"
				/>
			) : null}

			{items.length > 0 ? (
				<>
					<ul ref={flipRef} className="group mx-4 divide-y divide-separator">
						{items.map((item) => (
							<BuyRow
								key={item.id}
								item={item}
								onToggle={() => void handleToggle(item)}
								onDelete={() => handleDelete(item)}
							/>
						))}
					</ul>

					<div className="mt-4 flex flex-col gap-2 px-4">
						<Pressable
							className="cta w-full"
							disabled={importing || checked.length === 0}
							onClick={() => void handleImport()}
						>
							<IconGlyph glyph={GLYPHS.fridge} size={16} />
							{importing ? '入库中' : `买回的 ${checked.length} 样入库`}
						</Pressable>

						{checked.length > 0 ? (
							<Pressable className="cta-quiet w-full" onClick={() => void handleClearChecked()}>
								清掉已买
							</Pressable>
						) : null}
					</div>
				</>
			) : null}
		</div>
	);
}

/* ------------------------------------------------------------------ */

function BuyRow({
	item,
	onToggle,
	onDelete,
}: {
	item: BuyItem;
	onToggle: () => void;
	onDelete: () => void;
}): JSX.Element {
	const { rowRef, isOpen } = useSwipeReveal({
		revealWidth: REVEAL_WIDTH,
		onFullSwipe: onDelete,
	});

	return (
		<li
			ref={rowRef}
			data-flip-id={item.id}
			data-stagger
			className="relative overflow-hidden bg-surface"
		>
			{/* 收起状态必须 aria-hidden 且不可点击：读屏会为每行念一遍"删掉"，
			 * 透明按钮也仍然能被点到 */}
			<div
				data-swipe-actions
				aria-hidden={!isOpen}
				className={`absolute inset-y-0 right-0 flex opacity-0 ${
					isOpen ? '' : 'pointer-events-none'
				}`}
				style={{ width: REVEAL_WIDTH }}
			>
				<button
					type="button"
					tabIndex={isOpen ? 0 : -1}
					onClick={onDelete}
					className="flex w-full flex-col items-center justify-center gap-1 bg-danger-soft text-danger"
				>
					<IconGlyph glyph={GLYPHS.delete} size={17} weight="bold" />
					<span className="text-[11px] font-medium">删掉</span>
				</button>
			</div>

			<button
				type="button"
				data-swipe-content
				onClick={onToggle}
				className="relative flex w-full items-center gap-3 bg-surface px-4 py-3 text-left"
				style={{ touchAction: 'pan-y' }}
			>
				<span
					className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors ${
						item.checked ? 'border-transparent bg-ok text-white' : 'border-separator'
					}`}
				>
					{item.checked ? <IconGlyph glyph={GLYPHS.check} size={13} weight="bold" /> : null}
				</span>

				<span className="min-w-0 flex-1">
					<span
						className={`block truncate text-[16px] ${
							item.checked ? 'text-label-2 line-through' : 'text-label'
						}`}
					>
						{item.name}
					</span>
					{item.amount ? (
						<span className="mt-0.5 block text-[12px] text-label-2">{item.amount}</span>
					) : null}
				</span>
			</button>
		</li>
	);
}
